import type { MaterialClass } from '@felix/domain';
import { Button, Select } from '@mantine/core';
import { IconCalendar, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { RequisitionStatusBadge } from './badges.js';
import classes from './Purchasing.module.css';
import {
  filterRequisitions,
  formatAmount,
  NO_FILTER,
  type RequisitionFilter,
  requisitionStatusOptions,
  sortItems,
} from './requisitions.js';
import type { PurchasingSource } from './source.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from './table.js';
import type { RequisitionList as List, RequisitionStatus, RequisitionSummary } from './types.js';

type Column =
  | 'requisitionNo'
  | 'vessel'
  | 'department'
  | 'requestedBy'
  | 'requiredDate'
  | 'status'
  | 'inStock'
  | 'total';

const SORT_VALUE: Record<Column, (item: RequisitionSummary) => string | number | null> = {
  requisitionNo: (item) => item.requisitionNo,
  vessel: (item) => item.vesselName,
  department: (item) => item.department,
  requestedBy: (item) => item.requestedBy.role ?? item.requestedBy.name,
  requiredDate: (item) => item.requiredDate,
  status: (item) => item.status,
  inStock: (item) => item.inStockLines ?? null,
  total: (item) => item.total.value,
};

const PAGE_SIZE = 10;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: List; vessels: PersonnelVessel[] };

/**
 * Talep listesi (`satin-alma-akisi.jpeg` "Material Requisition"): "New Requisition", Vessel,
 * Status ve Date Range süzgeçleri; Requisition No / Vessel / Department / Requested By / Required
 * Date / Status / Total tablosu ve sayfalama. Gemide yalnız o gemi; ofiste filo.
 */
export function RequisitionList({
  source,
  onOpen,
  onNew,
}: {
  source: PurchasingSource;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.list.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<RequisitionFilter>(NO_FILTER);
  const [page, setPage] = useState(1);
  // Varsayılan sıra sunucununki gibi: yeniden eskiye (numara)
  const [sort, setSort] = useState<SortState<Column>>({ key: 'requisitionNo', dir: 'desc' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const [list, vessels] = await Promise.all([source.requisitions(), source.vessels()]);
      setState({ kind: 'ready', list, vessels });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state : null;
  const items = useMemo(
    () =>
      ready
        ? sortItems(filterRequisitions(ready.list.items, filter), sort.dir, SORT_VALUE[sort.key])
        : [],
    [ready, filter, sort],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (next: Partial<RequisitionFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    setPage(1);
  };
  const ship = source.scope === 'ship';
  // Talep gemide açılır (ADR 0026 §1): ofiste düğme yok, süzgeçler satırın başından
  const canCreate = Boolean(ready?.list.canCreate && source.editor);
  const baseCurrency = ready?.list.baseCurrency ?? 'USD';

  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k(ship ? 'descriptionShip' : 'descriptionWeb')} />

      <Panel flush aria-label={k('table')}>
        <div className={classes.toolbar} data-single={canCreate ? undefined : true}>
          {canCreate ? (
            <Button leftSection={<IconPlus size={16} />} onClick={onNew}>
              {k('new')}
            </Button>
          ) : null}
          <form
            className={classes.filters}
            aria-label={k('filters')}
            onSubmit={(event) => event.preventDefault()}
          >
            <Select
              label={k('vessel')}
              data={[
                { value: '', label: k('allVessels') },
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={ship ? (ready?.vessels[0]?.id ?? '') : (filter.vesselId ?? '')}
              allowDeselect={false}
              readOnly={ship}
              // Gemide salt okunur seçici: ok yok (M6f §0.6)
              {...(ship ? { rightSection: null } : {})}
              onChange={(value) => change({ vesselId: value || null })}
            />
            <Select
              label={k('status')}
              data={[
                { value: '', label: k('allStatus') },
                ...requisitionStatusOptions(ship ? 'ship' : 'office').map((value) => ({
                  value,
                  label: t(`purchasing.statuses.${value}`),
                })),
              ]}
              value={filter.status ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ status: (value || null) as RequisitionStatus | null })}
            />
            {/* Talep türü (ADR 0122 §6): bölümden türer; Provisions kumanya talebidir */}
            <Select
              label={k('kind')}
              data={[
                { value: '', label: k('allKinds') },
                { value: 'provision', label: k('kindOptions.provision') },
                { value: 'stores', label: k('kindOptions.stores') },
              ]}
              value={filter.kind ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ kind: (value || null) as MaterialClass | null })}
            />
            {/* Ofisin incelemesi (ADR 0111 §A): onaylı ve revizyonsuz talepler */}
            {ship ? null : (
              <Select
                label={k('review')}
                data={[
                  { value: '', label: k('allReview') },
                  { value: 'needed', label: k('reviewOptions.needed') },
                  { value: 'revised', label: k('reviewOptions.revised') },
                ]}
                value={filter.review ?? ''}
                allowDeselect={false}
                onChange={(value) =>
                  change({ review: (value || null) as 'needed' | 'revised' | null })
                }
              />
            )}
            <fieldset className={classes.range}>
              {/* Süzgeç talep gününe göredir; tablodaki tarih istenen tarihtir: görünür etiket */}
              <legend className={classes.label}>
                {k('dateRange')} ({k('dateRangePlaceholder')})
              </legend>
              <div className={classes.rangeBox}>
                <IconCalendar size={16} aria-hidden />
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateFrom')}
                  value={filter.from}
                  maxDate={filter.to ?? undefined}
                  onChange={(value) => change({ from: value })}
                />
                <span aria-hidden>–</span>
                <DayInput
                  variant="unstyled"
                  size="xs"
                  rightSection={null}
                  aria-label={k('dateTo')}
                  value={filter.to}
                  minDate={filter.from ?? undefined}
                  onChange={(value) => change({ to: value })}
                />
              </div>
            </fieldset>
          </form>
        </div>
        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={ship ? 8 : 9} label={k('table')} />
        ) : (ready?.list.items.length ?? 0) === 0 ? (
          <EmptyState
            title={k('emptyTitle')}
            description={k(source.editor ? 'emptyShip' : 'emptyWeb')}
          />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setFilter(NO_FILTER);
              setPage(1);
            }}
          />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    <th scope="col" className={classes.index}>
                      {k('columns.no')}
                    </th>
                    {(
                      [
                        'requisitionNo',
                        'vessel',
                        'department',
                        'requestedBy',
                        'requiredDate',
                        'status',
                      ] as const
                    ).map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                        {...(column === 'requisitionNo' ? { className: classes.key } : {})}
                      />
                    ))}
                    {/* Stokta mevcudu olan kalem sayısı (ADR 0111 §A): ofisin önüne düşer */}
                    {ship ? null : (
                      <SortableTh
                        column="inStock"
                        label={k('inStock')}
                        sort={sort}
                        onSort={setSort}
                        numeric
                      />
                    )}
                    <SortableTh
                      column="total"
                      label={k('columns.total', { currency: baseCurrency })}
                      sort={sort}
                      onSort={setSort}
                      numeric
                    />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item, index) => (
                    <tr key={item.id}>
                      <td className={classes.index}>{(current - 1) * PAGE_SIZE + index + 1}</td>
                      <td className={classes.key}>
                        <a
                          className={classes.link}
                          href={`?id=${item.id}`}
                          aria-label={k('open', { no: item.requisitionNo })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.requisitionNo}
                        </a>
                      </td>
                      <td>{item.vesselName}</td>
                      <td>
                        {t(`purchasing.departments.${item.department}`, {
                          defaultValue: item.department,
                        })}
                      </td>
                      <td>{item.requestedBy.role ?? item.requestedBy.name}</td>
                      <td>{formatDay(item.requiredDate)}</td>
                      <td>
                        <RequisitionStatusBadge status={item.status} />
                        {/* Kimin onayı bekleniyor (ADR 0091) */}
                        {item.pendingRole &&
                        (item.status === 'pending_approval' ||
                          item.status === 'office_approval') ? (
                          <span className={classes.awaiting}>
                            {k(
                              item.status === 'office_approval' ? 'awaitingOffice' : 'awaitingShip',
                              { role: item.pendingRole },
                            )}
                          </span>
                        ) : null}
                        {!ship && item.review ? (
                          <span className={classes.awaiting}>
                            {k(`reviewOptions.${item.review}`)}
                          </span>
                        ) : null}
                      </td>
                      {ship ? null : (
                        <td
                          data-numeric
                          data-tone={(item.inStockLines ?? 0) > 0 ? 'info' : undefined}
                        >
                          {item.inStockLines === undefined
                            ? '—'
                            : k('inStockLines', {
                                count: item.inStockLines,
                                total: item.lineCount,
                              })}
                        </td>
                      )}
                      <td
                        data-numeric
                        title={item.total.value === null ? k('unknownTotal') : undefined}
                      >
                        {item.total.value === null
                          ? `${formatAmount(item.total.priced, locale)}*`
                          : formatAmount(item.total.value, locale)}
                        {item.currency !== baseCurrency ? ` ${item.currency}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}
